import React, { useEffect, useState } from 'react';
import config from '../config';
import useTranslation from './useTranslation';

// Utilità per gestire la logica di ripetizione intelligente
const PromptManager = {
  // Ottiene il numero di volte che il prompt è stato mostrato
  getPromptCount() {
    return parseInt(localStorage.getItem('installPromptCount') || '0');
  },

  // Incrementa il contatore
  incrementPromptCount() {
    const count = this.getPromptCount() + 1;
    localStorage.setItem('installPromptCount', count.toString());
    return count;
  },

  // Ottiene il timestamp del prossimo show
  getNextShowTime() {
    const nextShow = localStorage.getItem('installPromptNextShow');
    return nextShow ? parseInt(nextShow) : 0;
  },

  // Calcola il ritardo per ogni tentativo: 1, 3, 7 giorni
  getDelayForAttempt(attempt) {
    const delays = {
      1: 1 * 24 * 60 * 60 * 1000,      // 1 giorno
      2: 3 * 24 * 60 * 60 * 1000,      // 3 giorni  
      3: 7 * 24 * 60 * 60 * 1000,      // 7 giorni
    };
    return delays[attempt] || 0;
  },

  // Imposta quando mostrare il prossimo prompt
  setNextShow(responseType) {
    const count = this.getPromptCount();
    
    if (responseType === 'not-now') {
      if (count >= 3) {
        // Dopo 3 tentativi, stop definitivo
        localStorage.setItem('installPromptDismissed', 'permanently');
      } else {
        const delay = this.getDelayForAttempt(count);
        const nextShow = Date.now() + delay;
        localStorage.setItem('installPromptNextShow', nextShow.toString());
      }
    }
  },

  // Verifica se il prompt può essere mostrato
  canShowPrompt() {
    // Se è stato rifiutato definitivamente
    if (localStorage.getItem('installPromptDismissed') === 'permanently') {
      return false;
    }

    // Se è troppo presto per il prossimo show
    const nextShow = this.getNextShowTime();
    if (nextShow && Date.now() < nextShow) {
      return false;
    }

    // Se ha superato il limite di 3 tentativi
    const count = this.getPromptCount();
    if (count >= 3) {
      localStorage.setItem('installPromptDismissed', 'permanently');
      return false;
    }

    return true;
  },

  // Registra che il prompt è stato mostrato
  markPromptShown() {
    this.incrementPromptCount();
  }
};

const InstallPrompt = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // Per animazione di discesa
  const [currentImageSrc, setCurrentImageSrc] = useState(config.assets.icon);
  const [showImage, setShowImage] = useState(true);

  useEffect(() => {
    // Controlla se l'app è già installata
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches || 
          window.navigator.standalone === true) {
        setIsInstalled(true);
        return true;
      }
      return false;
    };

    // Verifica se il prompt può essere mostrato
    if (checkIfInstalled() || !PromptManager.canShowPrompt()) {
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
      PromptManager.markPromptShown();
      
      // Animazione con ritardo per far scendere il pannello
      setTimeout(() => {
        setIsVisible(true);
      }, 100);
    };

    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setIsInstalled(true);
      setShowInstallButton(false);
      setDeferredPrompt(null);
      localStorage.setItem('installPromptDismissed', 'installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    try {
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        console.log('Utente ha accettato di installare la PWA');
        localStorage.setItem('installPromptDismissed', 'installed');
      } else {
        console.log('Utente ha rifiutato di installare la PWA');
        PromptManager.setNextShow('not-now');
      }
      
      setDeferredPrompt(null);
      setIsVisible(false);
      setTimeout(() => setShowInstallButton(false), 300); // Aspetta l'animazione
    } catch (error) {
      console.error('Errore durante il prompt di installazione:', error);
    }
  };

  const handleNotNowClick = () => {
    setIsVisible(false);
    setTimeout(() => setShowInstallButton(false), 300); // Aspetta l'animazione
    PromptManager.setNextShow('not-now');
  };

  // Non mostrare se l'app è già installata o se non c'è il prompt
  if (isInstalled || !showInstallButton) return null;

  return (
    <div 
      className={`position-fixed top-0 start-0 end-0 ${isVisible ? 'translate-y-0' : 'translate-y-n100'}`}
      style={{ 
        zIndex: 1050,
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.3s ease-out',
        backgroundColor: 'white',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }}
    >
      <div className="container-fluid">
        <div className="row py-4 px-3">
          {/* Icona a sinistra */}
          {showImage && (
            <div className="col-auto">
              <img 
                src={currentImageSrc}
                alt="App Icon"
                style={{ width: '48px', height: '48px' }}
                className="rounded"
                onError={(e) => {
                  if (currentImageSrc === config.assets.icon) {
                    // Prima prova con il logo
                    setCurrentImageSrc(config.assets.logo);
                  } else if (currentImageSrc === config.assets.logo) {
                    // Se fallisce anche il logo, nascondi l'immagine
                    setShowImage(false);
                  }
                }}
              />
            </div>
          )}
          
          {/* Contenuto principale */}
          <div className="col">
            {/* Titolo e descrizioni */}
            <div className="mb-3">
              <h6 className="fw-bold text-dark mb-1">{t('installaApp')}</h6>
              <div className="text-muted small mb-1">{t('installaAppDescrizione')}</div>
              <div className="text-muted small">{t('installaAppDettaglio')}</div>
            </div>
            
            {/* Bottoni allineati a sinistra */}
            <div className="d-flex justify-content-start gap-2">
              <button
                className="btn btn-primary px-4 py-2"
                style={{ borderRadius: '25px', fontWeight: '500' }}
                onClick={handleInstallClick}
              >
                {t('installa')}
              </button>
              <button
                className="btn btn-outline-secondary px-3 py-2"
                style={{ borderRadius: '25px', fontWeight: '500' }}
                onClick={handleNotNowClick}
              >
                {t('nonOra')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;